/**
 * Pagination de la boîte de réception.
 *
 * Le backend (`GET /v1/messagerie/inbox`) pagine par tranches de 25 et renvoie
 * `{ messages, page, totalPages, totalMessages, nonLus }`. Le frontend n'envoyait
 * aucun `page` et ignorait `totalPages` : seule la page 0 était affichée, donc au
 * plus 25 mails, quel que soit le volume réel de la boîte.
 *
 * Ce module isole la logique d'accumulation des pages pour qu'elle reste testable
 * hors de React.
 *
 * Seule la boîte de réception est paginée côté serveur : les autres dossiers lisent
 * la table `courrier` et renvoient une liste complète.
 */

/** Dossiers dont l'endpoint accepte un paramètre `page`. */
export const DOSSIERS_PAGINES = ['inbox'];

export function estPagine(folder) {
  return DOSSIERS_PAGINES.includes(folder);
}

/**
 * Normalise la charge utile d'un dossier, paginé ou non.
 *
 * `data` est le contenu de `res.data.data` : soit un tableau nu (dossiers non
 * paginés), soit l'objet `InboxPageResponse`.
 */
export function lirePage(data) {
  if (Array.isArray(data)) {
    return { messages: data, page: 0, totalPages: 1, totalMessages: data.length };
  }
  const messages = data?.messages || data?.content || [];
  const page = Number.isInteger(data?.page) ? data.page : 0;
  // Une réponse sans totalPages (endpoint non paginé, ou forme inattendue) ne doit
  // pas laisser croire qu'il reste des pages à charger : on se rabat sur la page
  // courante + 1, ce qui rend `resteDesPages` faux.
  const totalPages = Number.isInteger(data?.totalPages) ? data.totalPages : page + 1;
  const totalMessages = Number.isInteger(data?.totalMessages)
    ? data.totalMessages
    : messages.length;
  return { messages, page, totalPages, totalMessages };
}

/**
 * Fusionne une nouvelle page dans la liste déjà affichée, en éliminant les doublons.
 *
 * La déduplication n'est pas cosmétique : la pagination IMAP porte sur des index
 * (1..N) et non sur des identifiants. Si un mail arrive entre deux requêtes, N se
 * décale et la page suivante rechevauche la précédente. Sans dédup, le même mail
 * apparaîtrait deux fois - et React signalerait des clés dupliquées.
 *
 * Le premier exemplaire rencontré gagne : les pages arrivent du plus récent au plus
 * ancien, et la page déjà affichée est la plus fraîche.
 */
export function fusionnerPages(existants, nouveaux) {
  const vus = new Set();
  const sortie = [];
  for (const conv of [...(existants || []), ...(nouveaux || [])]) {
    if (!conv || vus.has(conv.id)) continue;
    vus.add(conv.id);
    sortie.push(conv);
  }
  return sortie;
}

/** Reste-t-il des pages à charger après `page` ? */
export function resteDesPages({ page, totalPages }) {
  return Number.isInteger(page) && Number.isInteger(totalPages) && page + 1 < totalPages;
}
